import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { OcClient } from "../../types/oc";

interface ClientSelectorProps {
  clients: OcClient[];
  selectedId: string | null; // billingId de la razón social elegida (null si ninguna)
  selectedLabel: string; // texto del botón ("" → placeholder)
  isNew: boolean; // whether the ad-hoc "Nuevo cliente" option is active
  onPick: (client: OcClient) => void;
  onPickNew: () => void;
  disabled?: boolean;
}

// Custom searchable client dropdown — MUI rebuild of the OCs-builder selector:
// live search (razón social/empresa/CIF, case-insensitive), "Por defecto"/"A→Z" sort
// toggle, click-outside close, and a "+ Nuevo cliente…" ad-hoc option.
//
// Las opciones son razones sociales (CompanyBilling) agrupadas por empresa, igual que el
// selector de la factura: lo que se elige decide los datos fiscales que se congelan en la
// OC; la OC se sigue ligando a la empresa (companyId).
const ClientSelector: React.FC<ClientSelectorProps> = ({
  clients,
  selectedId,
  selectedLabel,
  isNew,
  onPick,
  onPickNew,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");
  const [sortAlpha, setSortAlpha] = useState(true);

  const open = Boolean(anchorEl);

  // Los campos se leen con fallback a "": una opcion incompleta debe dejar el desplegable
  // menos util, nunca tumbar la pagina entera con un error de render.
  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.companyLabel ?? "").toLowerCase().includes(q) ||
          (c.fiscalName ?? "").toLowerCase().includes(q) ||
          (c.taxId ?? "").toLowerCase().includes(q)
      );
    }
    if (sortAlpha) {
      list.sort(
        (a, b) =>
          (a.companyLabel ?? "").localeCompare(b.companyLabel ?? "", "es") ||
          (a.fiscalName ?? "").localeCompare(b.fiscalName ?? "", "es")
      );
    }
    return list;
  }, [clients, search, sortAlpha]);

  // Agrupa por empresa (companyLabel) preservando el orden ya calculado.
  const groupedClients = useMemo(() => {
    const groups: { companyLabel: string; items: OcClient[] }[] = [];
    for (const c of filteredClients) {
      const label = c.companyLabel ?? "";
      const last = groups[groups.length - 1];
      if (last && last.companyLabel === label) {
        last.items.push(c);
      } else {
        groups.push({ companyLabel: label, items: [c] });
      }
    }
    return groups;
  }, [filteredClients]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch("");
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const displayText = isNew
    ? t("oc.selector.new")
    : selectedLabel || t("oc.selector.select");

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color:
            isNew || !selectedLabel ? theme.palette.neutral["600"] : "inherit",
          borderColor: theme.palette.neutral["300"],
        }}
      >
        <Typography
          variant="pSmall"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {displayText}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: anchorEl?.clientWidth, mt: 0.5 } },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t("oc.selector.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <Typography variant="pSmallest" color={theme.palette.neutral["600"]}>
            {t("oc.selector.sort")}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortAlpha ? "alpha" : "default"}
            onChange={(_e, v) => {
              if (v !== null) setSortAlpha(v === "alpha");
            }}
          >
            <ToggleButton value="default" sx={{ textTransform: "none" }}>
              {t("oc.selector.sortDefault")}
            </ToggleButton>
            <ToggleButton value="alpha" sx={{ textTransform: "none" }}>
              {t("oc.selector.sortAlpha")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <List sx={{ maxHeight: "34rem", overflowY: "auto", py: 0 }}>
          {groupedClients.map((group) => (
            <li key={group.companyLabel}>
              <ul style={{ padding: 0 }}>
                <ListSubheader
                  sx={{
                    bgcolor: theme.palette.neutral["100"],
                    lineHeight: "2rem",
                    fontWeight: 700,
                  }}
                >
                  {group.companyLabel}
                </ListSubheader>
                {group.items.map((c) => (
                  <ListItemButton
                    key={c.billingId || c.companyId}
                    selected={
                      !isNew &&
                      Boolean(c.billingId) &&
                      c.billingId === selectedId
                    }
                    onClick={() => {
                      onPick(c);
                      handleClose();
                    }}
                  >
                    <ListItemText
                      primary={c.fiscalName || c.companyLabel}
                      secondary={c.taxId || undefined}
                      secondaryTypographyProps={{
                        color: theme.palette.neutral["600"],
                        variant: "pSmallest",
                      }}
                    />
                  </ListItemButton>
                ))}
              </ul>
            </li>
          ))}
          {filteredClients.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
                {t("oc.selector.noResults")}
              </Typography>
            </Box>
          )}
        </List>

        <Divider />
        <ListItemButton
          onClick={() => {
            onPickNew();
            handleClose();
          }}
          sx={{ color: theme.palette.primary.main }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          <ListItemText primary={t("oc.selector.new")} />
        </ListItemButton>
      </Popover>
    </Box>
  );
};

export default ClientSelector;
